/**
 * Master Data Management (MDM) Service
 *
 * Purpose: API service layer for all MDM operations including CRUD, import/export, and context management
 * Architecture: Service class built on top of base API service with typed methods for MDM endpoints
 * Dependencies: apiService, MDM types, file handling utilities
 * Author: Development Team
 * Created: 2025-01-27
 */

import { PermissionKey } from '../constants/permissionKeys';
import {
  MasterDataValue,
  MasterDataValueAlias,
  MasterDataValueCreate,
  MasterDataValueUpdate,
  MasterDataImportBatch,
  ExportRequest,
  ConflictData,
  AliasCheckDuplicateResponse,
  ReactivateValueRequest,
  TenantContext,
  GlobalValueDependencies,
  ValueListFilters,
  ValueOrderSourceResponse,
  FileUploadResponse,
  ApiResponse,
} from '../types/mdm';
import { apiService } from './api';

/**
 * MDM Service Configuration
 */
interface MDMServiceConfig {
  baseUrl?: string;
  timeout?: number;
  retryAttempts?: number;
}

/**
 * Why an import upload was refused, from FastAPI's `detail`: a string, or
 * `{message, errors: [{row, message}]}` for a file that couldn't be parsed (ORCH-361).
 */
export const importErrorMessage = (error: any): string => {
  const detail = error?.response?.data?.detail;
  if (typeof detail === 'string') return detail;
  if (detail && typeof detail === 'object') {
    const reasons = Array.isArray(detail.errors)
      ? detail.errors.map((e: { message?: string }) => e?.message).filter(Boolean)
      : [];
    if (reasons.length) return reasons.join('; ');
    if (detail.message) return detail.message;
  }
  return error?.message || 'Failed to upload import file';
};

/**
 * Master Data Management Service Class
 *
 * Purpose: Provides comprehensive API interface for all MDM operations
 * Architecture: TypeScript class with method grouping for different MDM functionalities
 */
class MDMService {
  private config: MDMServiceConfig;
  private readonly API_PREFIX = '/v1/mdm';

  /**
   * Initialize MDM Service
   *
   * @param config - Optional service configuration
   */
  constructor(config: MDMServiceConfig = {}) {
    this.config = {
      timeout: 30000,
      retryAttempts: 3,
      ...config,
    };
  }

  // =============================================================================
  // VALUE CRUD OPERATIONS
  // =============================================================================

  /**
   * Create a new master data value
   *
   * @param valueData - Data for creating the value
   * @returns Promise resolving to created value
   * @throws {Error} If creation fails or duplicate detected
   */
  async createValue(valueData: MasterDataValueCreate): Promise<MasterDataValue> {
    try {
      const response = await apiService.api.post<MasterDataValue>(
        `${this.API_PREFIX}/values`,
        valueData
      );
      return response.data;
    } catch (error: any) {
      // Handle conflict errors specially
      if (error.response?.status === 409) {
        const conflictData = error.response.data?.detail || error.response.data;
        const conflictError = new Error(conflictData.message || 'Conflict detected');
        (conflictError as any).conflict = conflictData;
        (conflictError as any).isConflict = true;
        throw conflictError;
      }

      throw new Error(
        error.response?.data?.message || error.message || 'Failed to create master data value'
      );
    }
  }

  /**
   * Get master data values with optional filtering
   *
   * Note: Returns raw UUIDs in meta_data for list field references.
   * Use MDM cache service (useMDMLookup) for UUID-to-name resolution.
   *
   * @param filters - Optional filters for the query
   * @returns Promise resolving to array of values
   */
  async getValues(filters?: ValueListFilters): Promise<MasterDataValue[]> {
    try {
      const params = new URLSearchParams();

      if (filters?.fielddef_id) params.append('fielddef_id', filters.fielddef_id);
      if (filters?.tenant_id) params.append('tenant_id', filters.tenant_id);
      if (filters?.parent_value_id !== undefined) {
        // Support filtering by parent for hierarchical fields
        params.append('parent_value_id', filters.parent_value_id);
      }
      if (filters?.include_inactive !== undefined)
        params.append('include_inactive', String(filters.include_inactive));
      if (filters?.include_deleted !== undefined)
        params.append('include_deleted', String(filters.include_deleted));
      if (filters?.for_permission) params.append('for_permission', filters.for_permission);
      if (filters?.offer) params.append('offer', 'true');
      // ORCH-400: one repeatable term per related field with a selection
      filters?.filter_by?.forEach((entry) => params.append('filter_by', entry));

      const response = await apiService.api.get<MasterDataValue[]>(
        `${this.API_PREFIX}/values?${params.toString()}`
      );
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || error.message || 'Failed to fetch master data values'
      );
    }
  }

  /**
   * Get a single master data value by ID
   *
   * @param valueId - ID of the value to retrieve
   * @param options - Optional parameters for the request
   * @param options.include_hierarchy_path - Include full hierarchy path
   * @param options.tenant_id - Tenant context for hierarchy resolution
   * @returns Promise resolving to the value
   */
  async getValue(
    valueId: string,
    options?: {
      include_hierarchy_path?: boolean;
      tenant_id?: string;
    }
  ): Promise<MasterDataValue> {
    try {
      const params = new URLSearchParams();

      if (options?.include_hierarchy_path) {
        params.append('include_hierarchy_path', 'true');
      }
      if (options?.tenant_id) {
        params.append('tenant_id', options.tenant_id);
      }

      const url = params.toString()
        ? `${this.API_PREFIX}/values/${valueId}?${params.toString()}`
        : `${this.API_PREFIX}/values/${valueId}`;

      const response = await apiService.api.get<MasterDataValue>(url);
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || error.message || 'Failed to fetch master data value'
      );
    }
  }

  /**
   * Update an existing master data value
   *
   * @param valueId - ID of the value to update
   * @param updateData - Data to update
   * @param tenantId - Tenant context for the operation
   * @returns Promise resolving to updated value
   */
  async updateValue(
    valueId: string,
    updateData: MasterDataValueUpdate,
    tenantId?: string
  ): Promise<MasterDataValue> {
    try {
      const url = tenantId
        ? `${this.API_PREFIX}/values/${valueId}?tenant_id=${encodeURIComponent(tenantId)}`
        : `${this.API_PREFIX}/values/${valueId}`;

      const response = await apiService.api.put<MasterDataValue>(url, updateData);
      return response.data;
    } catch (error: any) {
      // Handle 409 Conflict responses (duplicates)
      if (error.response?.status === 409) {
        const conflictData = error.response.data?.detail || error.response.data;
        const conflictError = new Error(conflictData.message || 'Duplicate value detected');
        (conflictError as any).isConflict = true;
        (conflictError as any).conflict = conflictData;
        throw conflictError;
      }

      // Handle other errors
      throw new Error(
        error.response?.data?.message || error.message || 'Failed to update master data value'
      );
    }
  }

  /**
   * Soft delete a master data value
   *
   * @param valueId - ID of the value to delete
   * @returns Promise resolving when deletion is complete
   */
  async deleteValue(valueId: string): Promise<void> {
    try {
      await apiService.api.delete(`${this.API_PREFIX}/values/${valueId}`);
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || error.message || 'Failed to delete master data value'
      );
    }
  }

  // =============================================================================
  // LIST ORDER (ORCH-383)
  // =============================================================================

  /**
   * Where a list's order comes from in the active tenant, and whether the caller may
   * reorder it (mdm.edit over the whole list, not a source field)
   */
  async getValueOrderSource(fielddefId: string): Promise<ValueOrderSourceResponse> {
    const response = await apiService.api.get<ValueOrderSourceResponse>(
      `${this.API_PREFIX}/field-definitions/${fielddefId}/value-order`
    );
    return response.data;
  }

  /**
   * Replace the active tenant's custom order for a list. `valueIds` must name every
   * non-deleted value once, using the GLOBAL id for an overridden value.
   */
  async setValueOrder(fielddefId: string, valueIds: string[]): Promise<MasterDataValue[]> {
    const response = await apiService.api.put<MasterDataValue[]>(
      `${this.API_PREFIX}/field-definitions/${fielddefId}/value-order`,
      { value_ids: valueIds }
    );
    return response.data;
  }

  /** Clear the active tenant's custom order: GLOBAL's order in a tenant, A→Z in GLOBAL */
  async resetValueOrder(fielddefId: string): Promise<void> {
    await apiService.api.delete(`${this.API_PREFIX}/field-definitions/${fielddefId}/value-order`);
  }

  // =============================================================================
  // DUPLICATE DETECTION AND RESOLUTION
  // =============================================================================

  /**
   * Check for duplicate values
   *
   * @param valueData - Data to check for duplicates
   * @returns Promise resolving to conflict data or null
   */
  async checkDuplicates(valueData: MasterDataValueCreate): Promise<ConflictData | null> {
    try {
      await apiService.api.post(`${this.API_PREFIX}/values/check-duplicate`, valueData);
      return null; // No conflict
    } catch (error: any) {
      if (error.response?.status === 409) {
        return error.response.data as ConflictData;
      }
      throw new Error(
        error.response?.data?.message || error.message || 'Failed to check for duplicates'
      );
    }
  }

  // =============================================================================
  // ALIAS OPERATIONS (ORCH-115)
  // =============================================================================

  /**
   * Add an alias to a master data value.
   *
   * Tenant users may attach a tenant-scoped alias to any value visible to them
   * — including GLOBAL values — without admin rights. The backend resolves the
   * underlying parent automatically when the value is OVERRIDDEN.
   *
   * Idempotent inherit: adding a spelling that already exists as a GLOBAL alias
   * on the same underlying value returns the existing GLOBAL row, not a new
   * tenant-scoped row.
   *
   * @throws Error with `.isConflict = true` and `.conflict` set when 409.
   */
  async addAlias(valueId: string, alias: string): Promise<MasterDataValueAlias> {
    try {
      const response = await apiService.api.post<MasterDataValueAlias>(
        `${this.API_PREFIX}/values/${valueId}/aliases`,
        { alias }
      );
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 409) {
        const detail = error.response.data?.detail || error.response.data;
        const conflictError = new Error(
          detail?.message || 'Alias collides with an existing value or alias'
        );
        (conflictError as any).conflict = detail?.details || detail;
        (conflictError as any).isConflict = true;
        throw conflictError;
      }
      throw new Error(
        error.response?.data?.detail?.message ||
          error.response?.data?.message ||
          error.message ||
          'Failed to add alias'
      );
    }
  }

  /**
   * Rename an existing alias. Caller must own the alias (tenant context must
   * match `alias.tenant_id`); GLOBAL aliases require admin in GLOBAL context.
   */
  async updateAlias(
    valueId: string,
    aliasId: string,
    alias: string
  ): Promise<MasterDataValueAlias> {
    try {
      const response = await apiService.api.patch<MasterDataValueAlias>(
        `${this.API_PREFIX}/values/${valueId}/aliases/${aliasId}`,
        { alias }
      );
      return response.data;
    } catch (error: any) {
      if (error.response?.status === 409) {
        const detail = error.response.data?.detail || error.response.data;
        const conflictError = new Error(
          detail?.message || 'Alias collides with an existing value or alias'
        );
        (conflictError as any).conflict = detail?.details || detail;
        (conflictError as any).isConflict = true;
        throw conflictError;
      }
      throw new Error(
        error.response?.data?.detail?.message ||
          error.response?.data?.message ||
          error.message ||
          'Failed to update alias'
      );
    }
  }

  /**
   * Soft-delete an alias. The DB partial unique index is on `is_deleted=false`,
   * so the spelling is freed for reuse immediately.
   */
  async deleteAlias(valueId: string, aliasId: string): Promise<void> {
    try {
      await apiService.api.delete(`${this.API_PREFIX}/values/${valueId}/aliases/${aliasId}`);
    } catch (error: any) {
      throw new Error(
        error.response?.data?.detail?.message ||
          error.response?.data?.message ||
          error.message ||
          'Failed to delete alias'
      );
    }
  }

  /**
   * Pre-flight collision check for live form validation.
   *
   * Always resolves (HTTP 200) — `is_duplicate: true` is a successful answer
   * to the question "is this taken?", not an error. State-mutating routes
   * (`addAlias` / `updateAlias`) return HTTP 409 with the same shape.
   */
  async checkAliasDuplicate(
    fielddefId: string,
    alias: string,
    excludeValueId?: string
  ): Promise<AliasCheckDuplicateResponse> {
    try {
      const response = await apiService.api.post<AliasCheckDuplicateResponse>(
        `${this.API_PREFIX}/values/check-alias-duplicate`,
        {
          fielddef_id: fielddefId,
          alias,
          exclude_value_id: excludeValueId,
        }
      );
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.detail?.message ||
          error.response?.data?.message ||
          error.message ||
          'Failed to check alias duplicate'
      );
    }
  }

  /**
   * Reactivate an inactive or deleted master data value
   *
   * @param valueId - ID of the value to reactivate
   * @param updateFields - Optional fields to update during reactivation
   * @returns Promise resolving to reactivated value
   */
  async reactivateValue(
    valueId: string,
    updateFields?: Record<string, any>
  ): Promise<MasterDataValue> {
    try {
      const request: ReactivateValueRequest = {
        update_fields: updateFields,
      };

      const response = await apiService.api.post<MasterDataValue>(
        `${this.API_PREFIX}/values/${valueId}/reactivate`,
        request
      );
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || error.message || 'Failed to reactivate master data value'
      );
    }
  }

  /**
   * Get dependencies for a GLOBAL value
   *
   * @param valueId - ID of the GLOBAL value
   * @returns Promise resolving to dependency information
   */
  async getGlobalValueDependencies(valueId: string): Promise<GlobalValueDependencies> {
    try {
      const response = await apiService.api.get<GlobalValueDependencies>(
        `${this.API_PREFIX}/values/${valueId}/dependencies`
      );
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || error.message || 'Failed to fetch value dependencies'
      );
    }
  }

  // =============================================================================
  // SEARCH FUNCTIONALITY
  // =============================================================================

  /**
   * Search master data values by term
   *
   * @param searchTerm - Term to search for
   * @param fielddef_id - Optional field definition filter
   * @param tenant_id - Optional tenant filter
   * @param limit - Maximum number of results (default: 50)
   * @returns Promise resolving to matching values
   */
  async searchValues(
    searchTerm: string,
    fielddef_id?: string,
    tenant_id?: string,
    limit: number = 50,
    for_permission?: PermissionKey,
    offer?: boolean,
    filter_by?: string[]
  ): Promise<MasterDataValue[]> {
    try {
      const params = new URLSearchParams({
        q: searchTerm,
        limit: String(limit),
      });

      if (fielddef_id) params.append('fielddef_id', fielddef_id);
      if (tenant_id) params.append('tenant_id', tenant_id);
      // ORCH-332: only values this permission reaches (needs fielddef_id)
      if (for_permission) params.append('for_permission', for_permission);
      // ORCH-366: a picker's offer request (needs fielddef_id)
      if (offer) params.append('offer', 'true');
      // ORCH-400: the picker's related-field constraints
      filter_by?.forEach((entry) => params.append('filter_by', entry));

      const response = await apiService.api.get<MasterDataValue[]>(
        `${this.API_PREFIX}/values/search?${params.toString()}`
      );
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || error.message || 'Failed to search master data values'
      );
    }
  }

  // =============================================================================
  // IMPORT OPERATIONS
  // =============================================================================

  /**
   * Upload file for bulk import
   *
   * @param fielddef_id - Field definition ID for the import
   * @param tenant_id - Target tenant ID
   * @param file - File to upload (CSV or Excel)
   * @returns Promise resolving to upload response with batch ID
   */
  async uploadImportFile(
    fielddef_id: string,
    tenant_id: string,
    file: File
  ): Promise<FileUploadResponse> {
    try {
      const formData = new FormData();
      formData.append('file', file);

      const response = await apiService.api.post<FileUploadResponse>(
        `${this.API_PREFIX}/import/${fielddef_id}?tenant_id=${tenant_id}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
          // Increase timeout for file uploads
          timeout: this.config.timeout || 30000,
        }
      );
      return response.data;
    } catch (error: any) {
      throw new Error(importErrorMessage(error));
    }
  }

  /**
   * Get import batch status
   *
   * @param batchId - ID of the import batch
   * @returns Promise resolving to batch information
   */
  async getImportBatchStatus(batchId: string): Promise<MasterDataImportBatch> {
    try {
      const response = await apiService.api.get<MasterDataImportBatch>(
        `${this.API_PREFIX}/import-batches/${batchId}/status`
      );
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || error.message || 'Failed to fetch import batch status'
      );
    }
  }

  // =============================================================================
  // EXPORT OPERATIONS
  // =============================================================================

  /**
   * Export master data values to file
   *
   * @param exportRequest - Export configuration
   * @returns Promise resolving to file blob
   */
  async exportValues(exportRequest: ExportRequest): Promise<Blob> {
    try {
      const response = await apiService.api.post(`${this.API_PREFIX}/export`, exportRequest, {
        responseType: 'blob',
        timeout: this.config.timeout || 30000,
      });

      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || error.message || 'Failed to export master data values'
      );
    }
  }

  /**
   * Download exported file
   *
   * @param blob - File blob from export
   * @param filename - Suggested filename
   */
  downloadFile(blob: Blob, filename: string): void {
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  }

  // =============================================================================
  // CONTEXT MANAGEMENT
  // =============================================================================

  /**
   * Get current tenant context for MDM operations
   *
   * @returns Promise resolving to tenant context information
   */
  async getTenantContext(): Promise<TenantContext> {
    try {
      const response = await apiService.api.get<{ status: string; data: any }>(
        `${this.API_PREFIX}/context`
      );

      // Transform the rich response structure to match TenantContext interface
      const contextData = response.data.data;
      return {
        current_tenant_id: contextData.current_tenant.tenant_id,
        available_tenants: contextData.available_tenants.map((t: any) => t.tenant_id),
        can_access_global: contextData.user_permissions.can_edit_global,
      };
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || error.message || 'Failed to fetch tenant context'
      );
    }
  }

  /**
   * Switch tenant context for MDM operations
   *
   * @param tenant_id - Target tenant ID
   * @returns Promise resolving when context switch is complete
   */
  async switchTenantContext(tenant_id: string): Promise<ApiResponse> {
    try {
      const response = await apiService.api.post<ApiResponse>(`${this.API_PREFIX}/context/switch`, {
        tenant_id,
      });
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || error.message || 'Failed to switch tenant context'
      );
    }
  }

  // =============================================================================
  // VIEW CONFIGURATION
  // =============================================================================

  /**
   * Get view configuration for a model
   *
   * Purpose: Fetches view configuration for any model type with related fields
   * Architecture: Uses model-focused metadata endpoint for universal view retrieval
   * Logic: Calls Metadata API /models/{model_id}/view endpoint with view_type parameter
   * Input:
   *   - modelId: Model identifier (for MDM, this is the fielddef_id like 'fd_fd_vendor')
   *   - tenantId: Tenant context for the view
   *   - viewType: Type of view ('edit-modal', 'table', 'list', 'detail')
   * Output: Promise resolving to ViewConfiguration with ordered related fields
   * Exceptions: Throws error if view configuration fetch fails
   *
   * Note: For field definition models, modelId is the fielddef_id (e.g., 'fd_fd_vendor')
   *       For other models, modelId is the model identifier (e.g., 'campaign', 'vendor')
   *
   * @param modelId - Model identifier (fielddef_id for field definitions)
   * @param tenantId - Tenant ID for view context
   * @param viewType - View type: 'edit-modal', 'table', 'list', or 'detail'
   * @returns Promise resolving to view configuration
   */
  async getView(
    modelId: string,
    tenantId: string,
    viewType: 'edit-modal' | 'table' | 'list' | 'detail'
  ): Promise<any> {
    try {
      const response = await apiService.api.get(`/metadata/models/${modelId}/view`, {
        params: {
          view_type: viewType,
          tenant_id: tenantId,
        },
      });
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message ||
          error.message ||
          `Failed to fetch ${viewType} view configuration for model ${modelId}`
      );
    }
  }

  /**
   * Get modal view configuration with related fields for create/edit forms
   *
   * Purpose: Convenience method for fetching modal view configuration
   * Logic: Calls getView with 'edit-modal' view type
   * Input:
   *   - fieldDefId: Field definition ID (used as model_id)
   *   - tenantId: Tenant context for the view
   * Output: Promise resolving to ViewConfiguration with ordered related fields
   * Exceptions: Throws error if view configuration fetch fails
   *
   * @param fieldDefId - Field definition ID
   * @param tenantId - Tenant ID for view context
   * @returns Promise resolving to view configuration
   */
  async getModalView(fieldDefId: string, tenantId: string): Promise<any> {
    return this.getView(fieldDefId, tenantId, 'edit-modal');
  }

  /**
   * Get table view configuration with related fields for table display
   *
   * Purpose: Convenience method for fetching table view configuration
   * Logic: Calls getView with 'table' view type
   * Input:
   *   - fieldDefId: Field definition ID (used as model_id)
   *   - tenantId: Tenant context for the view
   * Output: Promise resolving to ViewConfiguration with ordered related fields
   * Exceptions: Throws error if view configuration fetch fails
   *
   * @param fieldDefId - Field definition ID
   * @param tenantId - Tenant ID for view context
   * @returns Promise resolving to view configuration
   */
  async getTableView(fieldDefId: string, tenantId: string): Promise<any> {
    return this.getView(fieldDefId, tenantId, 'table');
  }

  // =============================================================================
  // UTILITY METHODS
  // =============================================================================

  /**
   * Validate file format for import
   *
   * @param file - File to validate
   * @returns True if file format is supported
   */
  validateImportFile(file: File): { valid: boolean; message?: string } {
    const maxSize = 20 * 1024 * 1024; // 20MB
    const allowedTypes = [
      'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet', // .xlsx
      'application/vnd.ms-excel', // .xls
      'text/csv', // .csv
      'application/csv', // .csv (alternate MIME type)
    ];

    if (file.size > maxSize) {
      return {
        valid: false,
        message: 'File size exceeds 20MB limit',
      };
    }

    if (!allowedTypes.includes(file.type)) {
      const extension = file.name.split('.').pop()?.toLowerCase();
      if (!['xlsx', 'xls', 'csv'].includes(extension || '')) {
        return {
          valid: false,
          message: 'File must be Excel (.xlsx, .xls) or CSV (.csv) format',
        };
      }
    }

    return { valid: true };
  }

  /**
   * Format value name with scope indicator
   *
   * @param value - Master data value
   * @returns Formatted name with scope prefix
   */
  formatValueWithScope(value: MasterDataValue): string {
    const scopePrefix = {
      GLOBAL: '[G] ',
      LOCAL: '[L] ',
      OVERRIDDEN: '[O] ',
    };

    return `${scopePrefix[value.scope_info.scope] || ''}${value.name}`;
  }

  /**
   * Get scope color for UI display
   *
   * @param scope - Scope type
   * @returns Material-UI color name
   */
  getScopeColor(
    scope: 'GLOBAL' | 'LOCAL' | 'OVERRIDDEN'
  ): 'primary' | 'secondary' | 'warning' | 'error' | 'info' | 'success' {
    const colorMap = {
      GLOBAL: 'primary' as const,
      LOCAL: 'secondary' as const,
      OVERRIDDEN: 'warning' as const,
    };

    return colorMap[scope] || 'secondary';
  }

  /**
   * Check if current user can edit value based on scope and permissions
   *
   * @param value - Master data value
   * @param currentTenant - Current tenant context
   * @returns True if value can be edited
   */
  canEditValue(value: MasterDataValue, _currentTenant?: string): boolean {
    // Use scope_info from the value which includes server-side permission checks
    return value.scope_info.can_edit;
  }

  /**
   * Revert an OVERRIDDEN master data value back to its GLOBAL parent
   *
   * @param valueId - UUID of the OVERRIDDEN value to revert
   * @returns Promise resolving to revert confirmation with global value
   */
  async revertOverrideValue(valueId: string): Promise<any> {
    try {
      const response = await apiService.api.delete(`${this.API_PREFIX}/values/${valueId}/revert`);
      return response.data;
    } catch (error: any) {
      throw new Error(
        error.response?.data?.message || error.message || 'Failed to revert override value'
      );
    }
  }

  /**
   * Check if current user can delete value based on scope and permissions
   *
   * @param value - Master data value
   * @param currentTenant - Current tenant context
   * @returns True if value can be deleted
   */
  canDeleteValue(value: MasterDataValue, _currentTenant?: string): boolean {
    // Use scope_info from the value which includes server-side permission checks
    return value.scope_info.can_delete;
  }

  /**
   * Check if value can be reverted (is OVERRIDDEN)
   *
   * @param value - Master data value
   * @returns True if value can be reverted
   */
  canRevertValue(value: MasterDataValue): boolean {
    return value.scope_info?.scope === 'OVERRIDDEN' && value.global_value_id != null;
  }

  // =============================================================================
  // HIERARCHY UTILITY METHODS
  // =============================================================================

  /**
   * Get all direct children of a parent value
   *
   * Purpose: Fetch immediate children in hierarchy
   * Logic: Calls getValues with parent_value_id filter
   *
   * @param parentValueId - ID of parent value (null for root level)
   * @param fielddef_id - Field definition ID
   * @param tenant_id - Tenant context
   * @returns Promise resolving to array of child values
   */
  async getChildren(
    parentValueId: string | null,
    fielddef_id: string,
    tenant_id: string
  ): Promise<MasterDataValue[]> {
    return this.getValues({
      fielddef_id,
      tenant_id,
      parent_value_id: parentValueId || undefined,
      include_inactive: false,
      include_deleted: false,
    });
  }

  /**
   * Get all descendants of a value recursively
   *
   * Purpose: Fetch entire subtree below a value
   * Logic: Recursively fetches children and their descendants
   *
   * @param valueId - ID of parent value
   * @param fielddef_id - Field definition ID
   * @param tenant_id - Tenant context
   * @param maxDepth - Maximum recursion depth (default 10)
   * @returns Promise resolving to flat array of all descendants
   */
  async getDescendants(
    valueId: string,
    fielddef_id: string,
    tenant_id: string,
    maxDepth: number = 10
  ): Promise<MasterDataValue[]> {
    const descendants: MasterDataValue[] = [];
    const visited = new Set<string>();

    const fetchLevel = async (parentId: string, depth: number): Promise<void> => {
      if (depth >= maxDepth || visited.has(parentId)) {
        return;
      }

      visited.add(parentId);

      const children = await this.getChildren(parentId, fielddef_id, tenant_id);

      for (const child of children) {
        descendants.push(child);
        await fetchLevel(child.id, depth + 1);
      }
    };

    await fetchLevel(valueId, 0);
    return descendants;
  }

  /**
   * Validate parent-child relationship
   *
   * Purpose: Check if assigning parent would create circular reference
   * Logic:
   * - Checks if childId is in the hierarchy path of parentId
   * - Prevents circular references
   *
   * @param parentValueId - Proposed parent value ID
   * @param childValueId - Value to be set as child
   * @param fielddef_id - Field definition ID
   * @param tenant_id - Tenant context
   * @returns Promise resolving to validation result
   */
  async validateParentChild(
    parentValueId: string,
    childValueId: string,
    fielddef_id: string,
    tenant_id: string
  ): Promise<{ valid: boolean; reason?: string }> {
    try {
      // Cannot set self as parent
      if (parentValueId === childValueId) {
        return {
          valid: false,
          reason: 'Cannot set value as its own parent',
        };
      }

      // Get descendants of child value
      const descendants = await this.getDescendants(childValueId, fielddef_id, tenant_id);

      // Check if proposed parent is a descendant of child
      const isCircular = descendants.some((desc) => desc.id === parentValueId);

      if (isCircular) {
        return {
          valid: false,
          reason: 'Would create circular reference - proposed parent is a descendant of this value',
        };
      }

      return { valid: true };
    } catch (error: any) {
      console.error('Validation error:', error);
      return {
        valid: false,
        reason: error.message || 'Validation failed',
      };
    }
  }

  /**
   * Build hierarchy tree from flat list of values
   *
   * Purpose: Convert flat array to nested tree structure
   * Logic: Groups values by parent_value_id and builds tree
   *
   * @param values - Flat array of master data values
   * @param rootParentId - Parent ID for root level (null for true root)
   * @returns Array of root values with nested children
   */
  buildHierarchyTree(
    values: MasterDataValue[],
    rootParentId: string | null = null
  ): (MasterDataValue & { children?: MasterDataValue[] })[] {
    const childrenMap = new Map<string | null, MasterDataValue[]>();

    // Group values by parent
    for (const value of values) {
      const parentId = value.parent_value_id || null;
      if (!childrenMap.has(parentId)) {
        childrenMap.set(parentId, []);
      }
      childrenMap.get(parentId)!.push(value);
    }

    // Build tree recursively
    const buildNode = (
      value: MasterDataValue
    ): MasterDataValue & { children?: MasterDataValue[] } => {
      const children = childrenMap.get(value.id) || [];
      return {
        ...value,
        children: children.length > 0 ? children.map(buildNode) : undefined,
      };
    };

    // Get root values
    const rootValues = childrenMap.get(rootParentId) || [];
    return rootValues.map(buildNode);
  }
}

// Export singleton instance
export const mdmService = new MDMService();
export default mdmService;
